/**
 * Product card advanced: option gallery (variant-option-advanced style).
 * - Hover: updates card featured image + price (from thumb data); restore on pointer leave.
 * - Click: treats thumb as new main product — updates image, price, compare-at price, title,
 *   main product link, and moves ari-option-gallery__thumb--current to clicked thumb.
 *   Uses thumb data only (no variant hallucination). Restore then keeps this selection.
 * - Thumbs load via Storefront API search (parent-title-siblings.js); listeners use delegation so late-rendered thumbs work.
 */
(function () {
  const cardSelector = '[data-product-card-advanced]';
  const thumbSelector = '.product-card-advanced-option-thumb';
  const hoverPriceSelector = '[data-product-card-advanced-hover-price]';
  const galleryImgSelector = '.card-gallery img';
  const titleSelector = '.product-card-advanced__title';
  const titleLinkSelector = '.product-card-advanced__title-link';
  const mainCardLinkSelector = 'a.product-card__link';
  const mainCardPrice = '.product-card-advanced__price .price';
  const mainCardComparePrice = '.product-card-advanced__price .compare-at-price';
  const cardContentSelector = '.product-card__content';
  const cardAdvancedContentSelector = '.product-card-advanced__content';
  const colorToggleSelector = '[data-product-card-advanced-color-toggle]';
  const hoverOverlaySelector = '[data-product-card-advanced-hover-overlay]';
  const badgeContainerSelector = '.product-card-advanced__badge';
  const badgeSelector = '.product-badges__badge';
  const mobileMediaQuery = window.matchMedia('(max-width: 749px)');

  /**
   * @param {string | undefined} priceAmountStr
   * @param {string | undefined} compareAtAmountStr
   * @returns {string} e.g. "20% off", or "" if not a valid discount
   */
  function formatPercentOffBadge(priceAmountStr, compareAtAmountStr) {
    const p = parseFloat(String(priceAmountStr ?? ''));
    const c = parseFloat(String(compareAtAmountStr ?? ''));
    if (Number.isNaN(p) || Number.isNaN(c) || !(c > p) || c <= 0) return '';
    const pct = Math.round(((c - p) / c) * 100);
    return pct > 0 ? `${pct}% off` : '';
  }

  /**
   * Prefer "% off" for automatic sale badges (sibling thumbs); keep metafield / sold-out text.
   * @param {HTMLElement} thumb
   * @returns {{ text: string; scheme: string }}
   */
  function resolveThumbBadgeDisplay(thumb) {
    const scheme = thumb.dataset.badgeScheme || '';
    const rawText = (thumb.dataset.badgeText || '').trim();
    const available = thumb.dataset.available === 'true';
    if (!available) return { text: rawText, scheme };

    const pct = formatPercentOffBadge(thumb.dataset.priceAmount, thumb.dataset.compareAtAmount);
    if (pct && thumb.dataset.badgeFromSaleFallback === 'true') return { text: pct, scheme };

    return { text: rawText, scheme };
  }

  /**
   * @param {Element} el
   */
  function getCard(el) {
    return el.closest(cardSelector);
  }

  /**
   * @param {Element} card
   * @param {boolean} available
   */
  function setQuickAddDisplay(card, available) {
    const content = /** @type {HTMLElement | null} */ (card.querySelector(cardContentSelector));
    if (!content) return;

    const display = available ? 'flex' : 'none';
    content.style.setProperty('--quick-add-display', display);
    content.style.setProperty('--quick-add-mobile-display', display);
    content.style.setProperty('--quick-add-mobile-opacity', available ? '1' : '0');
  }

  /**
   * @param {Element} card
   * @param {string} badgeText
   * @param {string} badgeScheme
   */
  function setBadge(card, badgeText, badgeScheme) {
    const existingContainer = /** @type {HTMLElement | null} */ (card.querySelector(badgeContainerSelector));
    const normalizedText = (badgeText || '').trim();
    if (!existingContainer) return;

    if (!normalizedText) {
      const existingBadge = /** @type {HTMLElement | null} */ (existingContainer.querySelector(badgeSelector));
      if (existingBadge) existingBadge.remove();
      return;
    }

    let badge = /** @type {HTMLElement | null} */ (existingContainer.querySelector(badgeSelector));
    if (!badge) {
      badge = document.createElement('div');
      badge.classList.add('product-badges__badge', 'product-badges__badge--rectangle');
      existingContainer.appendChild(badge);
    }

    badge.className = `product-badges__badge product-badges__badge--rectangle${badgeScheme ? ` color-${badgeScheme}` : ''}`;
    badge.textContent = normalizedText;
  }

  /**
   * @param {Element} card
   * @returns {HTMLImageElement | null}
   */
  function getFirstGalleryImage(card) {
    return /** @type {HTMLImageElement | null} */ (card.querySelector(galleryImgSelector));
  }

  /**
   * @param {Element} gallery
   * @param {Node | null} node
   */
  function isPointerInHoverInteractiveArea(gallery, node) {
    if (!node || !(node instanceof Node)) return false;
    if (gallery.contains(node)) return true;

    const card = getCard(gallery);
    const advancedContent = card?.querySelector(cardAdvancedContentSelector);
    if (!(advancedContent instanceof HTMLElement)) return false;

    const overlay = advancedContent.querySelector(hoverOverlaySelector);
    const colorTrigger = advancedContent.querySelector('[data-product-card-advanced-color-trigger]');
    return (
      (overlay instanceof HTMLElement && overlay.contains(node)) ||
      (colorTrigger instanceof HTMLElement && colorTrigger.contains(node))
    );
  }

  /**
   * @param {Element} card
   * @param {boolean} isOpen
   */
  function setMobileGalleryState(card, isOpen) {
    const advancedContent = card.querySelector(cardAdvancedContentSelector);
    if (!(advancedContent instanceof HTMLElement)) return;

    const toggle = advancedContent.querySelector(colorToggleSelector);
    if (toggle instanceof HTMLElement) {
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    advancedContent.classList.toggle('product-card-advanced__content--mobile-gallery-open', isOpen);
  }

  /**
   * Custom element: option gallery thumbs. Hover = preview image/price; click = set as main product.
   */
  class ProductCardAdvancedOptionGallery extends HTMLElement {
    constructor() {
      super();
      this._boundGalleryPointerEnter = this._handleGalleryPointerEnter.bind(this);
      this._boundGalleryPointerLeave = this._handleGalleryPointerLeave.bind(this);
      this._boundGalleryClick = this._handleGalleryClick.bind(this);
      this._boundGalleryKeydown = this._handleGalleryKeydown.bind(this);
      this._boundHandleColorToggle = this._handleColorToggle.bind(this);
      this._boundHandleDocumentClick = this._handleDocumentClick.bind(this);
      this._boundSiblingsLoaded = this._handleSiblingsLoaded.bind(this);
      this._boundOverlayPointerLeave = this._handleOverlayPointerLeave.bind(this);
      this._originalImage = null;
      /** @type {HTMLElement | null} */
      this._hoverOverlayEl = null;
    }

    connectedCallback() {
      if (this.getAttribute('data-has-color-option') !== 'true') return;

      this.addEventListener('pointerenter', this._boundGalleryPointerEnter, true);
      this.addEventListener('pointerleave', this._boundGalleryPointerLeave, true);
      this.addEventListener('click', this._boundGalleryClick, true);
      this.addEventListener('keydown', this._boundGalleryKeydown);
      this.addEventListener('ari-siblings-loaded', this._boundSiblingsLoaded);

      const card = getCard(this);
      if (!card) return;
      const colorToggle = card.querySelector(colorToggleSelector);
      if (colorToggle instanceof HTMLElement) {
        colorToggle.addEventListener('click', this._boundHandleColorToggle);
      }

      const advancedContent = card.querySelector(cardAdvancedContentSelector);
      const overlay = advancedContent?.querySelector(hoverOverlaySelector);
      if (overlay instanceof HTMLElement) {
        this._hoverOverlayEl = overlay;
        overlay.addEventListener('pointerleave', this._boundOverlayPointerLeave);
      }

      document.addEventListener('click', this._boundHandleDocumentClick);

      this._syncBadgeFromCurrentThumb();

      queueMicrotask(() => {
        this.dispatchEvent(new CustomEvent('ari-request-parent-title-hydrate', { bubbles: true }));
      });
    }

    disconnectedCallback() {
      this.removeEventListener('pointerenter', this._boundGalleryPointerEnter, true);
      this.removeEventListener('pointerleave', this._boundGalleryPointerLeave, true);
      this.removeEventListener('click', this._boundGalleryClick, true);
      this.removeEventListener('keydown', this._boundGalleryKeydown);
      this.removeEventListener('ari-siblings-loaded', this._boundSiblingsLoaded);

      const card = getCard(this);
      if (card) {
        const colorToggle = card.querySelector(colorToggleSelector);
        if (colorToggle instanceof HTMLElement) {
          colorToggle.removeEventListener('click', this._boundHandleColorToggle);
        }
      }

      if (this._hoverOverlayEl) {
        this._hoverOverlayEl.removeEventListener('pointerleave', this._boundOverlayPointerLeave);
        this._hoverOverlayEl = null;
      }

      document.removeEventListener('click', this._boundHandleDocumentClick);

      window.dispatchEvent(
        new CustomEvent('ari-parent-title-gallery-disconnected', { detail: { gallery: this } }),
      );
    }

    _handleSiblingsLoaded() {
      this._syncBadgeFromCurrentThumb();
    }

    _syncBadgeFromCurrentThumb() {
      const card = getCard(this);
      if (!card) return;
      const currentThumb = /** @type {HTMLElement | null} */ (
        this.querySelector(`${thumbSelector}.ari-option-gallery__thumb--current`)
      );
      if (!currentThumb) return;
      const badgeDisplay = resolveThumbBadgeDisplay(currentThumb);
      setBadge(card, badgeDisplay.text, badgeDisplay.scheme);
      setQuickAddDisplay(card, currentThumb.dataset.available === 'true');
    }

    /**
     * @param {PointerEvent} e
     */
    _handleGalleryPointerEnter(e) {
      if (mobileMediaQuery.matches) return;
      const target = e.target instanceof Element ? e.target : null;
      const thumb = /** @type {HTMLElement | null} */ (target?.closest(thumbSelector));
      if (!thumb || !this.contains(thumb)) return;

      const card = getCard(this);
      if (!card) return;

      const img = getFirstGalleryImage(card);
      if (img && !this._originalImage) {
        this._originalImage = {
          src: img.getAttribute('src'),
          srcset: img.getAttribute('srcset')
        };
      }

      this._applyThumbToCard(thumb, card);
    }

    /**
     * @param {PointerEvent} e
     */
    _handleGalleryPointerLeave(e) {
      if (mobileMediaQuery.matches) return;
      const related = e.relatedTarget instanceof Node ? e.relatedTarget : null;
      const staysInInteractiveArea = isPointerInHoverInteractiveArea(this, related);
      if (staysInInteractiveArea) return;
      this._restore();
    }

    /**
     * @param {PointerEvent} e
     */
    _handleOverlayPointerLeave(e) {
      if (mobileMediaQuery.matches) return;
      const related = e.relatedTarget instanceof Node ? e.relatedTarget : null;
      if (isPointerInHoverInteractiveArea(this, related)) return;
      this._restore();
    }

    /**
     * @param {MouseEvent} e
     */
    _handleGalleryClick(e) {
      const target = e.target instanceof Element ? e.target : null;
      const thumb = /** @type {HTMLElement | null} */ (target?.closest(thumbSelector));
      if (!thumb || !this.contains(thumb)) return;

      e.preventDefault();
      e.stopPropagation();

      const card = getCard(this);
      if (!card) return;

      this._applyThumbToCard(thumb, card);
      this._setCurrentThumb(thumb);
      this._persistAsMainProduct(thumb, card);
    }

    /**
     * @param {KeyboardEvent} e
     */
    _handleGalleryKeydown(e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const target = e.target instanceof Element ? e.target : null;
      const thumb = /** @type {HTMLElement | null} */ (target?.closest(thumbSelector));
      if (!thumb || !this.contains(thumb)) return;

      e.preventDefault();
      const card = getCard(this);
      if (!card) return;
      this._applyThumbToCard(thumb, card);
      this._setCurrentThumb(thumb);
      this._persistAsMainProduct(thumb, card);
    }

    /**
     * @param {HTMLElement} thumb
     * @param {Element} card
     */
    _applyThumbToCard(thumb, card) {
      const imageUrl = thumb.dataset.imageUrl;
      const price = thumb.dataset.price;
      const compareAtPrice = thumb.dataset.compareAtPrice || '';
      const img = getFirstGalleryImage(card);
      if (img) {
        if (!this._originalImage) {
          this._originalImage = {
            src: img.getAttribute('src'),
            srcset: img.getAttribute('srcset')
          };
        }
        if (imageUrl && img.currentSrc !== imageUrl) {
          img.removeAttribute('srcset');
          img.src = imageUrl;
        }
      }

      const hoverPriceEl = /** @type {HTMLElement | null} */ (card.querySelector(hoverPriceSelector));
      if (hoverPriceEl) {
        const priceSpan = /** @type {HTMLElement | null} */ (hoverPriceEl.querySelector('.price'));
        const compareSpan = /** @type {HTMLElement | null} */ (hoverPriceEl.querySelector('.compare-at-price'));
        if (priceSpan && price) {
          priceSpan.innerHTML = `<b>${price}</b>`;
        }
        if (compareSpan) {
          if (compareAtPrice) {
            compareSpan.textContent = compareAtPrice;
            compareSpan.hidden = false;
          } else {
            compareSpan.textContent = '';
            compareSpan.hidden = true;
          }
        }
      }
    }

    /**
     * @param {HTMLElement} thumb
     */
    _setCurrentThumb(thumb) {
      this.querySelectorAll(thumbSelector).forEach((t) => t.classList.remove('ari-option-gallery__thumb--current'));
      thumb.classList.add('ari-option-gallery__thumb--current');
    }

    /**
     * @param {HTMLElement} thumb
     * @param {Element} card
     */
    _persistAsMainProduct(thumb, card) {
      const imageUrl = thumb.dataset.imageUrl;
      const price = thumb.dataset.price;
      const compareAtPrice = thumb.dataset.compareAtPrice || '';
      const productUrl = thumb.dataset.productUrl || '';
      const title = thumb.dataset.title || thumb.getAttribute('aria-label') || '';
      const available = thumb.dataset.available;
      const badgeDisplay = resolveThumbBadgeDisplay(thumb);
      const badgeText = badgeDisplay.text;
      const badgeScheme = badgeDisplay.scheme;

      const img = getFirstGalleryImage(card);
      if (img && imageUrl) {
        this._originalImage = {
          src: imageUrl,
          srcset: img.getAttribute('srcset') || ''
        };
      }

      const hoverPriceEl = card.querySelector(mainCardPrice);
      if (hoverPriceEl && price) {
        hoverPriceEl.innerHTML = `<b>${price}</b>`;
      }

      const mainCardComparePriceEl = /** @type {HTMLElement | null} */ (card.querySelector(mainCardComparePrice));
      if (mainCardComparePriceEl) {
        if (compareAtPrice) {
          mainCardComparePriceEl.innerHTML = `${compareAtPrice}`;
          mainCardComparePriceEl.hidden = false;
        } else {
          mainCardComparePriceEl.innerHTML = '';
          mainCardComparePriceEl.hidden = true;
        }
      }

      const titleEl = card.querySelector(titleSelector);
      if (titleEl) titleEl.textContent = title;

      if (productUrl) {
        const titleLink = card.querySelector(titleLinkSelector);
        if (titleLink && titleLink instanceof HTMLAnchorElement) titleLink.href = productUrl;
        const mainLink = card.querySelector(mainCardLinkSelector);
        if (mainLink && mainLink instanceof HTMLAnchorElement) mainLink.href = productUrl;
      }

      const productId = thumb.dataset.productId;
      if (productId) card.setAttribute('data-product-id', productId);
      setBadge(card, badgeText, badgeScheme);
      setQuickAddDisplay(card, available === 'true');
    }

    _restore() {
      const card = getCard(this);
      if (!card) return;

      const img = getFirstGalleryImage(card);
      if (img && this._originalImage) {
        const { src, srcset } = this._originalImage;
        if (srcset) {
          img.setAttribute('srcset', srcset);
        } else {
          img.removeAttribute('srcset');
        }
      
        if (src) {
          img.src = src;
        }
      }

      const hoverPriceEl = /** @type {HTMLElement | null} */ (card.querySelector(hoverPriceSelector));
      if (hoverPriceEl) {
        const defaultPrice = hoverPriceEl.dataset.defaultPrice || '';
        const defaultCompare = hoverPriceEl.dataset.defaultCompare || '';
        const priceSpan = /** @type {HTMLElement | null} */ (hoverPriceEl.querySelector('.price'));
        const compareSpan = /** @type {HTMLElement | null} */ (hoverPriceEl.querySelector('.compare-at-price'));
        if (priceSpan) priceSpan.innerHTML = `<b>${defaultPrice}</b>`;
        if (compareSpan) {
          compareSpan.textContent = defaultCompare;
          compareSpan.hidden = !defaultCompare;
        }
      }
    }

    /**
     * @param {MouseEvent} e
     */
    _handleColorToggle(e) {
      if (!mobileMediaQuery.matches) return;
      e.preventDefault();
      e.stopPropagation();

      const card = getCard(this);
      if (!card) return;
      const advancedContent = card.querySelector(cardAdvancedContentSelector);
      if (!(advancedContent instanceof HTMLElement)) return;

      const isOpen = !advancedContent.classList.contains('product-card-advanced__content--mobile-gallery-open');
      setMobileGalleryState(card, isOpen);
    }

    /**
     * @param {MouseEvent} e
     */
    _handleDocumentClick(e) {
      if (!mobileMediaQuery.matches) return;

      const card = getCard(this);
      if (!card) return;
      const advancedContent = card.querySelector(cardAdvancedContentSelector);
      if (!(advancedContent instanceof HTMLElement)) return;
      if (!advancedContent.classList.contains('product-card-advanced__content--mobile-gallery-open')) return;

      const overlay = advancedContent.querySelector(hoverOverlaySelector);
      const toggle = advancedContent.querySelector(colorToggleSelector);
      const target = e.target instanceof Node ? e.target : null;

      if (target && (toggle?.contains(target) || overlay?.contains(target))) return;
      setMobileGalleryState(card, false);
    }
  }

  customElements.define('product-card-advanced-option-gallery', ProductCardAdvancedOptionGallery);
})();
